import { useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface MatchDetail {
  date: string; // ISO date string
  result: string | null | undefined; // 'W' or 'L'
  csr: number;
  opponent_name?: string | null;
  player_games_won?: number | null;
  opponent_games_won?: number | null;
  player_race?: number | null;
  opponent_race?: number | null;
  venue?: string | null;
  division_name?: string | null;
  is_makeup_match?: boolean | null;
  csr_before?: number | null;
  playerName?: string;
  playerColor?: string;
}

interface MatchDetailPopoverProps {
  detail: MatchDetail;
  /** Position in pixels relative to the nearest positioned ancestor (the chart container). */
  x: number;
  y: number;
  onClose: () => void;
  /** Optional callback to launch head-to-head view for this player vs opponent. */
  onHeadToHead?: (opponentName: string) => void;
}

function formatDate(isoDate: string): string {
  const date = new Date(isoDate + (isoDate.includes('T') ? '' : 'T12:00:00'));
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function MatchDetailPopover({ detail, x, y, onClose, onHeadToHead }: MatchDetailPopoverProps) {
  const ref = useRef<HTMLDivElement>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);
  const isWin = detail.result === 'W';
  const [style, setStyle] = useState<React.CSSProperties>({
    position: 'fixed',
    left: 0,
    top: 0,
    visibility: 'hidden',
  });

  // Convert container-relative (x, y) to viewport-fixed coordinates
  useLayoutEffect(() => {
    if (!ref.current || !anchorRef.current) return;
    const anchor = anchorRef.current.parentElement;
    if (!anchor) return;

    const containerRect = anchor.getBoundingClientRect();
    const popover = ref.current;
    const pw = popover.offsetWidth;
    const ph = popover.offsetHeight;
    const gap = 12;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Viewport-relative anchor point
    const anchorX = containerRect.left + x;
    const anchorY = containerRect.top + y;

    // Prefer above; flip below if not enough space
    const showBelow = anchorY - ph - gap < 8;
    const rawTop = showBelow ? anchorY + gap : anchorY - ph - gap;

    // Clamp within viewport
    const clampedLeft = Math.max(8, Math.min(anchorX - pw / 2, vw - pw - 8));
    const clampedTop = Math.max(8, Math.min(rawTop, vh - ph - 8));

    setStyle({
      position: 'fixed',
      left: clampedLeft,
      top: clampedTop,
      visibility: 'visible',
    });
  }, [x, y]);

  // Dismiss on click outside or Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    // Defer outside-click listener so the tap that opened the popover
    // doesn't immediately trigger close.
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 50);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onClose]);

  const hasScore =
    detail.player_games_won != null && detail.opponent_games_won != null;
  const hasRace = detail.player_race != null && detail.opponent_race != null;

  return (
    <>
      {/* Invisible anchor to measure container position */}
      <span ref={anchorRef} className="absolute left-0 top-0 w-0 h-0" />
      <div
        ref={ref}
        className="fixed z-[9999] w-[calc(100vw-2rem)] max-w-64 rounded-xl border border-slate-200 bg-white shadow-[0_8px_24px_rgba(0,0,0,0.12)] text-xs"
        style={style}
      >
      {/* Header */}
      <div className="flex items-center justify-between rounded-t-xl bg-slate-50 px-3 py-2 border-b border-slate-100">
        <div className="flex items-center gap-2 min-w-0">
          {detail.playerName && (
            <span
              className="h-2 w-2 flex-shrink-0 rounded-full"
                style={{ backgroundColor: detail.playerColor ?? '#6366f1' }}
              />
            )}
            <span className="font-semibold text-slate-700 truncate">
              {detail.playerName ?? formatDate(detail.date)}
            </span>
          </div>
          <button
            onClick={onClose}
            className="ml-2 flex-shrink-0 text-slate-400 hover:text-slate-600"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="px-3 py-2.5 space-y-1.5">
          {/* Date (shown if we have a player name in the header) */}
          {detail.playerName && (
            <div className="text-slate-500">{formatDate(detail.date)}</div>
          )}

          {/* Result + Opponent */}
          <div className="flex items-center gap-2">
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-bold ${
                isWin
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'bg-red-50 text-red-600'
              }`}
            >
              {isWin ? 'WIN' : 'LOSS'}
            </span>
            {detail.opponent_name ? (
              <span className="text-slate-700 font-medium truncate">
                vs. {detail.opponent_name}
              </span>
            ) : (
              <span className="text-slate-400 italic">Opponent unknown</span>
            )}
          </div>

          {/* Score */}
          {hasScore && (
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Score</span>
              <span className="font-semibold text-slate-700">
                {detail.player_games_won} – {detail.opponent_games_won}
              </span>
            </div>
          )}

          {/* Race */}
          {hasRace && (
            <div className="flex items-center justify-between">
              <span className="text-slate-500">Race</span>
              <span className="font-semibold text-slate-700">
                {detail.player_race} to {detail.opponent_race}
              </span>
            </div>
          )}

          {/* CSR before → after */}
          <div className="flex items-center justify-between">
            <span className="text-slate-500">CSR</span>
            <span className="font-semibold text-slate-700 flex items-center gap-1.5">
              {detail.csr_before != null && (
                <>
                  <span className="text-slate-400 font-normal">{detail.csr_before}</span>
                  <span className="text-slate-300">→</span>
                </>
              )}
              {detail.csr}
              {detail.csr_before != null && (
                <span className={`text-xs font-medium ${
                  detail.csr > detail.csr_before
                    ? 'text-emerald-600'
                    : detail.csr < detail.csr_before
                    ? 'text-red-500'
                    : 'text-slate-400'
                }`}>
                  {detail.csr > detail.csr_before ? '+' : ''}{detail.csr - detail.csr_before}
                </span>
              )}
            </span>
          </div>

          {/* Division */}
          {detail.division_name && (
            <div className="flex items-start justify-between gap-2">
              <span className="text-slate-500 flex-shrink-0">Division</span>
              <span className="text-slate-600 text-right leading-tight">{detail.division_name}</span>
            </div>
          )}

          {/* Venue */}
          {detail.venue && (
            <div className="flex items-start justify-between gap-2">
              <span className="text-slate-500 flex-shrink-0">Venue</span>
              <span className="text-slate-600 text-right leading-tight">{detail.venue}</span>
            </div>
          )}

          {/* Makeup match badge */}
          {detail.is_makeup_match && (
            <div className="pt-0.5">
              <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 border border-amber-200">
                Makeup match
              </span>
            </div>
          )}

          {/* Head to Head button */}
          {onHeadToHead && detail.opponent_name && (
            <div className="pt-1.5 border-t border-slate-100 mt-1.5">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onHeadToHead(detail.opponent_name!);
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onHeadToHead(detail.opponent_name!);
                }}
                className="w-full flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-medium text-brand-600 hover:text-brand-700 hover:bg-brand-50 rounded-lg transition-colors"
              >
                <span>⚔️</span>
                Head to Head
              </button>
            </div>
          )}
        </div>
        </div>
    </>
  );
}
